import React, { useState, useEffect, lazy, useMemo } from 'react';
import { ConfigProvider, App, Spin, notification, Progress } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { createBrowserRouter, RouterProvider, useLocation, Outlet, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationProvider } from './context/NotificationContext';
import { ExportProvider } from './context/ExportContext';
import AuthRouterWrapper from './components/auth/AuthRouterWrapper';
import AuthTimeoutManager from './components/auth/AuthTimeoutManager';
import SessionTimeoutModal from './components/auth/SessionTimeoutModal';
import SessionWarningModal from './components/auth/SessionWarningModal';
import LoginPage from './pages/LoginPage';
import ErrorBoundary, { ErrorFallback, RouteError } from './components/shared/ErrorBoundary';
import { createSafeLazyImport, setupGlobalChunkErrorHandler } from './utils/chunkLoadHandler';
import { initVersionCheck } from './utils/versionCheck';
import { AppLayoutWrapper, AdminLayoutWrapper, RootRedirect, CatchAllRedirect } from './components/layout/AppLayout';
import apiService from './services/apiService';
import { globalApiScheduler } from './api/globalApiScheduler';

// Lazy-loaded page components with safe error handling
const Dashboard = lazy(createSafeLazyImport(() => import('./pages/Dashboard')));
const InvoicesList = lazy(createSafeLazyImport(() => import('./pages/invoices/InvoicesList')));
const CreateInvoice = lazy(createSafeLazyImport(() => import('./pages/invoices/CreateInvoice')));
const PendingInvoices = lazy(createSafeLazyImport(() => import('./pages/invoices/PendingInvoices')));
const SubmittedInvoices = lazy(createSafeLazyImport(() => import('./pages/invoices/SubmittedInvoices')));
const UserManagement = lazy(createSafeLazyImport(() => import('./pages/UserManagement')));
const Settings = lazy(createSafeLazyImport(() => import('./pages/Settings')));
const MaintenanceModePage = lazy(createSafeLazyImport(() => import('./pages/admin/MaintenanceModePage')));
const NotificationDetails = lazy(createSafeLazyImport(() => import('./pages/NotificationDetails')));
const NotificationsList = lazy(createSafeLazyImport(() => import('./pages/NotificationsList')));
const SubmittedDocumentDetails = lazy(createSafeLazyImport(() => import('./pages/invoices/SubmittedDocumentDetails')));
const SubmissionDetails = lazy(createSafeLazyImport(() => import('./pages/invoices/SubmissionDetails')));
const SubmissionHistoryDetails = lazy(createSafeLazyImport(() => import('./pages/invoices/SubmissionHistoryDetails')));
const AuditLogs = lazy(createSafeLazyImport(() => import('./pages/AuditLogs')));
const InvoiceMonitoring = lazy(createSafeLazyImport(() => import('./pages/admin/InvoiceMonitoring')));
const SystemHealth = lazy(createSafeLazyImport(() => import('./pages/admin/SystemHealth')));

import './App.css'

// Role-based dashboard component - redirect admin to Invoice Monitoring, users to Dashboard
const RoleBasedDashboard = () => {
  const { user } = useSelector((state) => state.auth);
  if (user?.role === 'admin') {
    return <Navigate to="/admin/invoice-monitoring" replace />;
  }

  return <Dashboard />;
};

const IMPORT_NOTIF_KEY = 'auto-import-global';
const IMPORT_TERMINAL = new Set(['completed', 'completed_with_errors', 'error', 'failed', 'cancelled']);

const AutoImportGlobalNotification = () => {
  const location = useLocation();
  const autoImport = useSelector(state => state.autoImport);

  useEffect(() => {
    const session = autoImport.activeImportSession;
    const status = session?.status || autoImport.status || '';
    const isActive = (session && !IMPORT_TERMINAL.has(session.status)) || autoImport.isImporting;
    const isTerminal = IMPORT_TERMINAL.has(status);
    const onDashboard = location.pathname === '/dashboard';

    if (onDashboard) {
      notification.destroy(IMPORT_NOTIF_KEY);
      return;
    }

    if (!isActive && !isTerminal) return;

    const progress = Math.round(Number(autoImport.progress || session?.progress || 0));
    const message = autoImport.message || session?.message || '';
    const isSuccess = status === 'completed' || status === 'completed_with_errors';

    notification.open({
      key: IMPORT_NOTIF_KEY,
      message: isSuccess ? 'Auto-import complete' : isTerminal ? 'Auto-import stopped' : 'Auto-importing invoices',
      description: (
        <div>
          <div style={{ marginBottom: 6, fontSize: 12, color: '#595959' }}>{message}</div>
          {!isTerminal && <Progress percent={progress} size="small" status="active" />}
        </div>
      ),
      duration: isTerminal ? 8 : 0,
      placement: 'bottomRight',
      style: { width: 340, minWidth: 340 },
      icon: isSuccess
        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
        : isTerminal
          ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
          : <LoadingOutlined style={{ color: '#1677ff' }} />,
    });

    if (isTerminal) {
      const t = setTimeout(() => notification.destroy(IMPORT_NOTIF_KEY), 8000);
      return () => clearTimeout(t);
    }
  }, [autoImport, location.pathname]);

  return null;
};

// AppContent component that wraps all routes with auth components
const AppContent = () => {
  const location = useLocation();

  const isLoginPage = location.pathname === '/login';

  if (isLoginPage) {
    return <Outlet />;
  }

  return (
    <AuthRouterWrapper>
      <AuthTimeoutManager />
      <SessionTimeoutModal />
      <SessionWarningModal />
      <AutoImportGlobalNotification />
      <Outlet />
    </AuthRouterWrapper>
  );
};

// Create the router configuration
const createAppRouter = (isMaintenanceMode, maintenanceData) => {
  return createBrowserRouter([
    {
      path: "/",
      element: <AppContent />,
      errorElement: <RouteError />,
      children: [
        {
          path: "/login",
          element: (
            <PublicRouteWrapper isMaintenanceMode={isMaintenanceMode} maintenanceData={maintenanceData}>
              <LoginPage />
            </PublicRouteWrapper>
          ),
          errorElement: <RouteError />,
        },
        {
          index: true,
          element: <RootRedirect />,
          errorElement: <RouteError />,
        },
        {
          path: "/dashboard",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <RoleBasedDashboard />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/invoices",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <InvoicesList />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/invoices/create",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <CreateInvoice />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/invoices/edit/:invoiceId",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <CreateInvoice />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/invoices/pending",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <PendingInvoices />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/invoices/submitted",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <SubmittedInvoices />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/invoices/submitted/:uuid",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <SubmittedDocumentDetails />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/invoices/submissions/:submissionUid",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <SubmissionDetails />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/invoices/submission-history/:historyId",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <SubmissionHistoryDetails />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/users",
          element: <AdminLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <UserManagement />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/audit",
          element: <AdminLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <AuditLogs />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/admin/maintenance",
          element: <AdminLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <MaintenanceModePage />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/admin/invoice-monitoring",
          element: <AdminLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <InvoiceMonitoring />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/admin/system-health",
          element: <AdminLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <SystemHealth />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/settings",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <Settings />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/notifications",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <NotificationsList />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "/notifications/:id",
          element: <AppLayoutWrapper isMaintenanceMode={isMaintenanceMode} />,
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <NotificationDetails />,
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: "*",
          element: <CatchAllRedirect />,
          errorElement: <RouteError />,
        },
      ],
    },
  ]);
};

// Route wrapper that checks maintenance mode for public routes
const PublicRouteWrapper = ({ isMaintenanceMode, maintenanceData, children }) => {
  return React.cloneElement(children, {
    isMaintenanceMode,
    maintenanceData
  });
};

function RootApp() {
  useDispatch();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState(null);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  useEffect(() => {
    setupGlobalChunkErrorHandler();
    const cleanupVersionCheck = initVersionCheck();
    Sentry.logger.info('User triggered test log', { log_source: 'sentry_test' });

    return () => {
      cleanupVersionCheck();
    };
  }, []);

  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.get('bypassToken'); // Ensure bypass token is recognized from URL

        const response = await apiService.getMaintenanceStatus();
        if (response.data && response.data.data) {
          const status = response.data.data;
          const maintenanceEnabled = status.enabled;

          setMaintenanceData(status);
          setIsMaintenanceMode(maintenanceEnabled);
        }
      } catch (error) {
        console.error('Error checking maintenance status:', error);
        setIsMaintenanceMode(false);
        setMaintenanceData(null);
      } finally {
        setMaintenanceChecked(true);
      }
    };

    checkMaintenanceStatus();
  }, []);

  const customTheme = {
    token: {
      colorPrimary: '#1890ff',
      colorBgContainer: '#ffffff',
      colorText: '#33394c',
      colorTextSecondary: '#6f7687',
      borderRadius: 6,
      fontSize: 12,
      fontSizeLG: 14,
      fontSizeXL: 16,
      controlHeight: 30,
      controlHeightSM: 24,
      controlHeightLG: 34,
      padding: 12,
      paddingLG: 16,
      paddingMD: 12,
      margin: 12,
      marginLG: 16,
      marginMD: 12
    },
    components: {
      Alert: {
        fontSize: 12,
        paddingContentHorizontal: 10,
        paddingContentVerticalSM: 7
      },
      Badge: {
        fontSize: 11
      },
      Card: {
        headerFontSize: 13,
        headerFontSizeSM: 12,
        paddingLG: 14,
        padding: 12
      },
      Descriptions: {
        labelBg: '#fafafa',
        titleMarginBottom: 12
      },
      Form: {
        labelFontSize: 12,
        itemMarginBottom: 14
      },
      Menu: {
        fontSize: 12,
        itemHeight: 36,
        itemMarginBlock: 2
      },
      Modal: {
        titleFontSize: 15,
        paddingContentHorizontalLG: 18,
        paddingContentVerticalLG: 16
      },
      Pagination: {
        fontSize: 12,
        itemSize: 28,
        itemSizeSM: 24
      },
      Select: {
        fontSize: 12
      },
      Table: {
        cellFontSize: 12,
        cellFontSizeMD: 12,
        cellFontSizeSM: 12,
        cellPaddingBlock: 8,
        cellPaddingBlockMD: 7,
        cellPaddingBlockSM: 6,
        cellPaddingInline: 10,
        cellPaddingInlineMD: 8,
        cellPaddingInlineSM: 6,
        headerBg: '#fafafa',
        headerColor: '#262626',
        headerSplitColor: '#f0f0f0'
      },
      Tabs: {
        titleFontSize: 12,
        horizontalMargin: '0 0 10px 0'
      },
      Tag: {
        fontSizeSM: 11,
        lineHeightSM: 1.6
      },
      Typography: {
        titleMarginBottom: 10,
        titleMarginTop: 0
      }
    },
  };

  const router = useMemo(
    () => createAppRouter(isMaintenanceMode, maintenanceData),
    [isMaintenanceMode, maintenanceData]
  );

  // Stop a stray file-drop from navigating the window to the dropped file. Ant's Upload.Dragger only
  // prevents default on its own rectangle, so a CSV dropped on the surrounding padding (or anywhere
  // else) would otherwise make the browser open/download it. Scoped to file drags, and it only cancels
  // the browser's default navigation - a real dropzone's own handler runs first and still receives the
  // file, so drops on the dropzone keep working and drops that miss become a harmless no-op.
  useEffect(() => {
    const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const preventFileDropNavigation = (e) => { if (isFileDrag(e)) e.preventDefault(); };
    window.addEventListener('dragover', preventFileDropNavigation);
    window.addEventListener('drop', preventFileDropNavigation);
    return () => {
      window.removeEventListener('dragover', preventFileDropNavigation);
      window.removeEventListener('drop', preventFileDropNavigation);
    };
  }, []);

  useEffect(() => {
    const cancelScheduledRequests = () => {
      globalApiScheduler.cancelAll();
    };

    window.addEventListener('beforeunload', cancelScheduledRequests);
    return () => window.removeEventListener('beforeunload', cancelScheduledRequests);
  }, []);

  if (!maintenanceChecked) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <App>
      <ConfigProvider theme={customTheme}>
        <NotificationProvider>
          <ExportProvider>
          <ErrorBoundary>
            <Sentry.ErrorBoundary
              fallback={ErrorFallback}
              showDialog={false}
              onError={(error, errorInfo) => {
                console.error('Sentry ErrorBoundary caught an error:', error, errorInfo);
              }}
            >
              <RouterProvider router={router} />
            </Sentry.ErrorBoundary>
          </ErrorBoundary>
          </ExportProvider>
        </NotificationProvider>
      </ConfigProvider>
    </App>
  );
}

export default RootApp;
